import equal from "fast-deep-equal";
import { _Product, useCartDrawer, useGlobalProducts } from "global-stores";
import { ReactProductState } from "product";
import { FC, useCallback, useEffect, useRef, useState } from "react";

import { _Cart_fetch_api_Items } from "types";
import { JSONParse, shortUUID } from "utils";
import { StoreApi, UseBoundStore } from "zustand";

export const ProductSideEffects: FC = ({
  useProduct,
  productSection,
  cartDrawer,
  hasThemeSubscriptionSelector,
}) => {
  const cartItemRef = useRef<_Cart_fetch_api_Items[]>([]);
  const { cartData } = useCartDrawer(({ cartData }) => ({ cartData }));
  const [tryAgainCount, setTryAgainCount] = useState(0);
  const {
    selectedVariant,
    product,
    setSelectedSellingPlan,
    selectedSellingPlan,
    setProduct,
    formId,
    sideEffect,
  } = useProduct(
    ({
      selectedVariant,
      product,
      setSelectedSellingPlan,
      selectedSellingPlan,
      setProduct,
      formId,
      sideEffect,
    }) => ({
      formId,
      selectedVariant,
      product,
      setSelectedSellingPlan,
      selectedSellingPlan,
      setProduct,
      sideEffect,
    })
  );

  const productData = useRef<_Product>(
    window.transformProducts(
      JSONParse(
        (
          document.querySelector(`[data-product-json="${product?.handle}"]`) ??
          document.querySelector(`[data-product-json]`)
        ).innerHTML
      )
    )
  );

  useEffect(() => {
    const product = productData.current;

    if (!window.Shopify.designMode && !equal(cartData.items, cartItemRef.current)) {
      cartItemRef.current = cartData.items;
      if (!cartDrawer) {
        setProduct({
          ...product,
          variants: product?.variants?.map((variant) => {
            const lineItemQuantity = cartData?.items?.reduce((acc, lineItem) => {
              if (lineItem.variant_id === variant.id) {
                acc += lineItem.quantity;
              }
              return acc;
            }, 0);
            if (lineItemQuantity === 0) return variant;

            const inventory_quantity = variant.inventory_quantity - lineItemQuantity;
            return {
              ...variant,
              available:
                inventory_quantity > 0 ||
                variant.inventory_policy === "continue" ||
                variant.inventory_management !== "shopify",
              inventory_quantity,
            };
          }),
        });
      }
    }
  }, [cartData.items, cartDrawer, setProduct]);

  useEffect(() => {
    if (product.id !== productData.current.id) {
      productData.current = product;
    }
  }, [product]);

  useEffect(() => {
    if (window?.location?.pathname?.includes(`/products/${product?.handle ?? shortUUID()}`)) {
      const otherQueryParams = window.location.search
        .split(/[?&]/gi)
        .filter((param) => param && !/^variant=/gi.test(param))
        .filter(
          (param) => !hasThemeSubscriptionSelector || (param && !/^selling_plan=/gi.test(param))
        );

      window.history.replaceState(
        null,
        null,
        `?variant=${selectedVariant?.id}${
          hasThemeSubscriptionSelector && selectedSellingPlan
            ? `&selling_plan=${selectedSellingPlan?.id}`
            : ""
        }${otherQueryParams.map((param) => `&${param}`).join("")}`
      );
      window.selectedVariant = selectedVariant;
    }
  }, [hasThemeSubscriptionSelector, product?.handle, selectedSellingPlan, selectedVariant]);

  const handleHashChange = useCallback(() => {
    setTimeout(() => {
      const selling_plan_id =
        window.location.search
          ?.split(/[?&]/gi)
          ?.filter((param) => param && /^selling_plan=/gi.test(param))
          ?.map((searchParams) => searchParams.split("=")?.at(-1))?.[0] ?? "";

      const selling_plan = selectedVariant?.selling_plan_allocations?.find(
        (selling_plan) => selling_plan.id === +selling_plan_id
      );

      setSelectedSellingPlan(selling_plan);
    }, 100);
  }, [selectedVariant?.selling_plan_allocations, setSelectedSellingPlan]);

  useEffect(() => {
    if (hasThemeSubscriptionSelector) return;

    handleHashChange();
    setTimeout(() => {
      handleHashChange();
    }, 1000);
    setTimeout(() => {
      handleHashChange();
    }, 4000);
  }, [handleHashChange, hasThemeSubscriptionSelector]);

  useEffect(() => {
    if (hasThemeSubscriptionSelector) return;

    document.addEventListener("change", handleHashChange);
    return () => {
      document.removeEventListener("change", handleHashChange);
    };
  }, [handleHashChange, hasThemeSubscriptionSelector]);

  useEffect(() => {
    if (hasThemeSubscriptionSelector) return;

    const mutationObserver = new MutationObserver((e) => {
      e?.forEach((record) => {
        const value = (record.target as HTMLInputElement).value;
        if (!value) return;
        const selling_plan_id = value ? `&selling_plan=${value}` : "";

        const selling_plan = selectedVariant?.selling_plan_allocations?.find(
          (selling_plan) => selling_plan.id === +selling_plan_id
        );
        setSelectedSellingPlan(selling_plan);

        if (window.location.pathname.includes(`/products/${product?.handle ?? shortUUID()}`)) {
          const otherQueryParams = window.location.search
            .split(/[?&]/gi)
            .filter((param) => param && !/^variant=/gi.test(param))
            .filter((param) => param && !/^selling_plan=/gi.test(param));

          window.history.replaceState(
            null,
            null,
            `?variant=${selectedVariant?.id}${selling_plan_id}${otherQueryParams
              .map((param) => `&${param}`)
              .join("")}`
          );
        }
      });
    });

    const sellingPlans = [
      ...productSection.querySelectorAll(
        'input[name="selling_plan"]:not([data-selling-plan-output])'
      ),
    ];

    sellingPlans.forEach((appInput) => {
      mutationObserver.observe(appInput, {
        attributes: true,
      });
    });

    return () => {
      mutationObserver.disconnect();
    };
  }, [
    hasThemeSubscriptionSelector,
    product?.handle,
    productSection,
    selectedVariant?.id,
    selectedVariant?.selling_plan_allocations,
    setSelectedSellingPlan,
    tryAgainCount,
  ]);

  useEffect(() => {
    if (hasThemeSubscriptionSelector) return;

    const mutationObserver = new MutationObserver((e) => {
      e?.forEach((record) => {
        record.addedNodes.forEach((node) => {
          if (
            node?.parentElement?.querySelectorAll(
              'input[name="selling_plan"]:not([data-selling-plan-output])'
            )
          ) {
            setTryAgainCount((prev) => (prev += 1));
          }
        });
      });
    });

    mutationObserver.observe(productSection, {
      subtree: true,
      childList: true,
    });

    return () => {
      mutationObserver.disconnect();
    };
  }, [hasThemeSubscriptionSelector, productSection]);

  useEffect(() => {
    const blocks = productSection.querySelector<HTMLElement>("[data-product-blocks]");
    blocks?.setAttribute("data-product-blocks", product.handle);
  }, [product.handle, productSection]);

  useEffect(() => {
    if (selectedVariant?.available) {
      productSection.style.removeProperty("--display-variant-available");
      productSection.style.setProperty("--display-variant-unavailable", "none");
    } else {
      productSection.style.removeProperty("--display-variant-unavailable");
      productSection.style.setProperty("--display-variant-available", "none");
    }
  }, [productSection.style, selectedVariant?.available]);

  useEffect(() => {
    const buttons = document.querySelectorAll<HTMLButtonElement>("[data-product-add-to-cart]");
    const clickHandlers = new Map();

    buttons.forEach((button) => {
      const handler = () => {
        document
          .querySelector(
            `[data-product-blocks="${product?.handle}"] form[action="/cart/add"][id="${formId}"]`
          )
          .dispatchEvent(new Event("submit"));
        const loadingSpinner = button.querySelector("[data-loading-spinner]");
        loadingSpinner.classList.remove("opacity-0");

        const cartOpenHandler = () => {
          loadingSpinner.classList.add("opacity-0");
          document.removeEventListener("cart:open", cartOpenHandler);
        };
        document.addEventListener("cart:open", cartOpenHandler);
      };
      clickHandlers.set(button, handler);
      button.addEventListener("click", handler);
    });

    return () => {
      buttons.forEach((button) => {
        const handler = clickHandlers.get(button);
        if (handler) {
          button.removeEventListener("click", handler);
        }
      });
    };
  }, [formId, product?.handle]);

  useEffect(() => {
    productSection?.querySelectorAll<HTMLDivElement>("[data-product-block]").forEach((block) => {
      const hasTextContent = block.innerText.trim().length > 0;
      const hasPaymentTerms = block.querySelector("shopify-payment-terms") !== null;
      const hasContent = hasTextContent || hasPaymentTerms;
      const blockName = block.getAttribute("data-product-block") || "unnamed";

      if (blockName === "@app" || blockName === "payment_terms") {
        return;
      }

      if (hasContent) {
        block.classList.remove("!hidden");
        block.removeAttribute("data-empty-hidden");
      } else {
        block.classList.add("!hidden");
        block.setAttribute("data-empty-hidden", "");
      }
    });
  }, [selectedVariant, product, productSection, sideEffect]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      productSection?.querySelectorAll<HTMLDivElement>("[data-product-block]").forEach((block) => {
        const height = block.offsetHeight;

        if (height === 0) {
          block.classList.add("!hidden");
          block.setAttribute("data-empty-hidden", "");
        }
      });
    }, 3000);

    return () => clearTimeout(timeout);
  }, [selectedVariant, product, productSection]);

  useEffect(() => {
    if (!productSection) return;

    const mutationObserver = new MutationObserver((e) => {
      e?.forEach((record) => {
        const value = record;
        useProduct.getState().triggerSideEffect();
      });
    });

    const productBlocks = productSection?.querySelector(`[data-product-blocks]`);
    if (productBlocks) {
      mutationObserver.observe(productBlocks, {
        childList: true,
        subtree: true,
      });
    }

    const timeouts = [
      setTimeout(() => useProduct.getState().triggerSideEffect(), 500),
      setTimeout(() => useProduct.getState().triggerSideEffect(), 1000),
      setTimeout(() => useProduct.getState().triggerSideEffect(), 2000),
      setTimeout(() => useProduct.getState().triggerSideEffect(), 4000),
    ];

    return () => {
      mutationObserver.disconnect();
      timeouts.forEach((timeout) => clearTimeout(timeout));
    };
  }, [productSection, useProduct]);

  useEffect(() => {
    if (!productSection) return;

    const giftCardRecipient = productSection.querySelector(
      '[data-product-block="gift_card_recipient"]'
    );
    const giftCardRecipientEmail = productSection.querySelector<HTMLInputElement>(
      "[data-gift-card-recipient-email]"
    );

    if (giftCardRecipient) {
      giftCardRecipient.classList.toggle("hidden", !product?.gift_card);
    }

    const handleChange = (e) => {
      const giftCardRecipientEmailError = productSection.querySelector(
        "[data-gift-card-recipient-email-error]"
      );
      giftCardRecipientEmailError?.classList?.add("hidden");
    };

    if (giftCardRecipientEmail) {
      giftCardRecipientEmail.addEventListener("change", handleChange);
    }

    return () => {
      if (giftCardRecipientEmail) {
        giftCardRecipientEmail.removeEventListener("change", handleChange);
      }
    };
  }, [product?.gift_card, productSection]);

  useEffect(() => {
    const hasUpsells =
      product?._complementary?.length ||
      product?._related?.length ||
      Date.now() - (product?._upsell_fetched ?? 0) < 1000 * 60 * 30;

    if (!hasUpsells) {
      useGlobalProducts
        .getState()
        .getAsyncProductWithUpsells(product.handle, product.id)
        .then((data) => useProduct.getState().setProduct(data));
    }
  }, [
    product?._complementary,
    product?._related,
    product?._upsell_fetched,
    product.handle,
    product.id,
    useProduct,
  ]);

  return null;
};
